import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, Output } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { ApprovalStatusActionMap } from '@app/modules/database/models/registration-status-action.map'
import { ApprovalStatusText } from '@app/modules/database/models/registration-status-text.map'
import { ApprovalStatus } from '@app/modules/database/models/registration-status.enum'
import { Registration } from '@app/modules/database/models/registration.model'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { DialogComponent } from '@app/modules/shared/components/dialog/dialog.component'
import { ToastrService } from '@app/services/toastr.service'
import { WorkspaceRoleService } from '@auth/services/workspace-role.service'
import { NbDialogConfig, NbDialogService } from '@nebular/theme'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { Subject } from 'rxjs'
import { first, takeUntil } from 'rxjs/operators'
import { RevisionRequestModalComponent } from '../../../revision-request-modal/revision-request-modal.component'
import { RequestChangesHistoryComponent } from '../request-changes-history/request-changes-history.component'
/**
 * Displays a toggle switch or a drop down menu based on registration approval state
 */
@Component({
  selector: 'ngx-approval-statuses',
  templateUrl: './approval-statuses.component.html',
  styleUrls: ['./approval-statuses.component.scss'],
  imports: [CommonModule, MatIconModule, NgxTippyModule]
})
export class ApprovalStatusesComponent {
  @Input() registrant: Registration
  @Input() event: ServerRepresentedEvent
  @Input() form: Persona

  @Output() statusChange: EventEmitter<ApprovalStatus> = new EventEmitter<ApprovalStatus>()
  config: Partial<NbDialogConfig> = {}
  private _destroy$ = new Subject()

  constructor(private dialog: MatDialog, private dialogService: NbDialogService, private toastr: ToastrService, private workspaceRoleService: WorkspaceRoleService) {}

  ngOnInit() {
    this.config = {
      closeOnBackdropClick: true,
      closeOnEsc: true,
      context: {
        registrant: this.registrant,
        event: this.event
      },
      autoFocus: false
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  // Helpers for template (avoid arrow functions in templates)
  onApproveClick() {
    const approve = this.statusActions?.find((a) => a.name === 'approve')
    const approveToPay = this.statusActions?.find((a) => a.name === 'approve-to-pay')
    const chosen = approve && this.approveOrPay(approve) ? approve : approveToPay
    if (chosen && !chosen.disabled) {
      this.setStatus(chosen)
    }
  }

  onDeclineClick() {
    const decline = this.statusActions?.find((a) => a.name === 'decline')
    if (decline && !decline.disabled) {
      this.setStatus(decline)
    }
  }

  onRequestRevisionClick() {
    const req = this.statusActions?.find((a) => a.name === 'request-revision')
    if (req && !req.disabled) {
      this.setStatus(req)
    }
  }

  get statusText() {
    return ApprovalStatusText.get(this.registrant.entry_status)
  }

  get statusActions() {
    return ApprovalStatusActionMap.get(this.registrant.entry_status)
  }

  approveOrPay(action) {
    switch (action.name) {
      case 'approve': {
        if (!this.form.enable_payment || (this.form.enable_payment && this.registrant.payment_status !== 'none')) return true
        return false
      }
      case 'approve-to-pay': {
        if (this.form.enable_payment && this.registrant.payment_status === 'none') return true
        return false
      }
      default:
        return true
    }
  }

  /**
   * Sets the status of the current registration
   */
  setStatus(statusAction) {
    switch (statusAction.name) {
      case 'approve':
      case 'approve-to-pay': {
        this.statusChange.emit(statusAction.status)
        break
      }

      case 'request-revision': {
        const _revisionModalRef = this.dialogService.open(RevisionRequestModalComponent, this.config)
        _revisionModalRef.onClose.pipe(first(), takeUntil(this._destroy$)).subscribe((res) => {
          if (res?.subject || res?.message) {
            this.statusChange.emit(statusAction.status)
          }
        })
        break
      }

      case 'decline': {
        const dialogData = {
          headerText: `Are you sure?`,
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Decline',
          confirmButtonClass: 'delete',
          hideIcon: false
        }
        const _declineModalRef = this.dialog.open(DialogComponent, {
          minWidth: '544px',
          panelClass: 'matdialog',
          data: dialogData
        })
        _declineModalRef
          .afterClosed()
          .pipe(first(), takeUntil(this._destroy$))
          .subscribe((res) => {
            if (res === 'confirm') {
              this.statusChange.emit(statusAction.status)
            }
          })
        break
      }

      default:
        break
    }
  }

  openRequestHistory() {
    if (this.registrant['_source']['revision_requests'] === null) {
      this.toastr.warning('There is no request revision for that registrant', '')
      return
    }
    const _revisionModalRef = this.dialogService.open(RequestChangesHistoryComponent, this.config)
  }

  /**
   * Checks if user can change approval status
   * @returns boolean
   */
  get canChangeApprovalStatus(): boolean {
    return this.workspaceRoleService.canChangeApprovalStatus()
  }
}
